(() => {
  if (window.mlProductExperienceReady) return;
  window.mlProductExperienceReady = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const products = () => document.querySelectorAll('product-info.ml-premium-product');

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const headerOffset = () => {
    const header = document.querySelector('.section-header, sticky-header, header');
    return header ? Math.min(header.getBoundingClientRect().height, 160) + 12 : 18;
  };

  const formatOptionValue = (value = '') =>
    value
      .replace(/&quot;/gi, '"')
      .replace(/"/g, '')
      .replace(/(\d)\s*[xX]\s*(\d)/g, '$1 × $2')
      .replace(/\s+/g, ' ')
      .trim();

  const getProductState = (product) => {
    const sizeInput = product.querySelector('.ml-size-selector input[type="radio"]:checked');
    const size = formatOptionValue(sizeInput?.value || '');
    const isSample = /sample/i.test(size);
    const salePrice = product.querySelector('.price--large .price-item--sale .price-item--last');
    const regularPrice = product.querySelector('.price--large .price-item--regular');
    const price = (salePrice || regularPrice)?.textContent.replace(/\s+/g, ' ').trim() || '';
    return { size, isSample, price };
  };

  const arrangePurchaseControls = (product) => {
    const quantity = product.querySelector(
      '.product__info-container > .product-form__quantity, .product-form__buttons .ml-inline-quantity'
    );
    const submit = product.querySelector('.product-form__buttons .product-form__submit');
    if (!submit) return;

    const buttons = submit.parentElement;
    if (!buttons) return;

    buttons.classList.add('ml-purchase-grid');
    if (quantity) {
      quantity.classList.add('ml-inline-quantity');
      if (quantity.parentElement !== buttons || quantity.nextElementSibling !== submit) {
        buttons.insertBefore(quantity, submit);
      }
    }
  };

  const setGroupOpen = (group, open) => {
    group.classList.toggle('is-open', open);
    group.querySelector('[data-ml-size-group-toggle]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const updateSizeGroups = (product, openSelected = false) => {
    const selector = product.querySelector('.ml-size-selector');
    const groups = Array.from(product.querySelectorAll('[data-ml-size-group]'));
    if (!selector || !groups.length) return;

    const checked = product.querySelector('.ml-size-selector input[type="radio"]:checked');
    const selectedGroup = checked?.closest('[data-ml-size-group]');

    groups.forEach((group) => {
      const selectedLabel = group.querySelector('[data-ml-group-selected]');
      setText(selectedLabel, group === selectedGroup && checked ? formatOptionValue(checked.value) : '');
    });

    if (!mobileQuery.matches) {
      groups.forEach((group) => setGroupOpen(group, true));
      selector.dataset.mlGroupMode = 'desktop';
      return;
    }

    if (selector.dataset.mlGroupMode !== 'mobile') {
      groups.forEach((group) => setGroupOpen(group, group === (selectedGroup || groups[0])));
      selector.dataset.mlGroupMode = 'mobile';
      return;
    }

    if (!groups.some((group) => group.classList.contains('is-open'))) {
      setGroupOpen(selectedGroup || groups[0], true);
    } else if (openSelected && selectedGroup) {
      groups.forEach((group) => setGroupOpen(group, group === selectedGroup));
    }
  };

  const setupSampleProxy = (product) => {
    const sampleGroup = product.querySelector('.ml-sample-selector');
    if (!sampleGroup) return;

    // The actual sample selector is the control on every screen size.
    // On mobile, keep it directly below the custom-size card instead of
    // replacing it with a separate proxy button. This prevents the selector
    // from disappearing when Shopify updates the selected size/variant.
    product.classList.remove('has-ml-sample-proxy');
    product.querySelector('[data-ml-sample-proxy]')?.remove();

    if (!mobileQuery.matches) return;

    const customSizeBlock =
      product.querySelector('.ml-custom-request') ||
      product.querySelector('.ml-custom-size-card') ||
      product.querySelector('[data-ml-custom-size]') ||
      product.querySelector('.ml-custom-size') ||
      product.querySelector('.product__custom-size');

    if (customSizeBlock && sampleGroup.previousElementSibling !== customSizeBlock) {
      customSizeBlock.insertAdjacentElement('afterend', sampleGroup);
      return;
    }

    const sizeSelector = product.querySelector('.ml-size-selector');
    if (sizeSelector && !customSizeBlock && sampleGroup.parentElement !== sizeSelector) {
      sizeSelector.appendChild(sampleGroup);
    }
  };

  const setupMobilePurchaseBar = (product) => {
    if (!product.querySelector('.ml-size-selector') || product.querySelector('[data-ml-mobile-purchase-bar]')) return;

    const bar = document.createElement('div');
    bar.className = 'ml-mobile-purchase-bar';
    bar.dataset.mlMobilePurchaseBar = '';
    bar.setAttribute('aria-hidden', 'true');
    if (window.Shopify?.designMode) bar.classList.add('is-design-mode');
    bar.innerHTML = `<button type="button" class="ml-mobile-purchase-bar__button" data-ml-sticky-submit></button>`;
    product.appendChild(bar);
  };

  const updatePurchaseUI = (product) => {
    if (!product.querySelector('.ml-size-selector')) return;
    const state = getProductState(product);
    const submit = product.querySelector('.product-form__submit');
    const submitLabel = submit?.querySelector(':scope > span:first-child');
    const buttonPrice = state.price.replace(/\s*USD\b/i, '').trim();
    const desktopLabel = state.isSample
      ? `Add sample to bag${buttonPrice ? ` · ${buttonPrice}` : ''}`
      : state.size
        ? `Add ${state.size} rug to bag${buttonPrice ? ` · ${buttonPrice}` : ''}`
        : `Add rug to bag${buttonPrice ? ` · ${buttonPrice}` : ''}`;

    const mobileLabel = state.isSample
      ? `Add sample${buttonPrice ? ` · ${buttonPrice}` : ''}`
      : state.size
        ? `Add ${state.size}${buttonPrice ? ` · ${buttonPrice}` : ''}`
        : `Add to bag${buttonPrice ? ` · ${buttonPrice}` : ''}`;

    const activeLabel = mobileQuery.matches ? mobileLabel : desktopLabel;

    if (submit && !submit.disabled) setText(submitLabel, activeLabel);

    const proxy = product.querySelector('[data-ml-sample-proxy]');
    if (proxy) {
      proxy.classList.toggle('is-selected', state.isSample);
      proxy.setAttribute('aria-pressed', state.isSample ? 'true' : 'false');
      proxy.setAttribute('aria-label', state.isSample ? 'Color sample selected' : 'Order a $0.50 color sample');
    }

    const stickySubmit = product.querySelector('[data-ml-sticky-submit]');
    if (stickySubmit && submit) {
      stickySubmit.disabled = submit.disabled;
      const disabledLabel = submitLabel?.textContent.trim() || 'Unavailable';
      const compactPrice = state.price.replace(/\s*USD\b/i, '').replace(/\.00(?=\s|$)/, '');
      const stickyLabel = state.isSample
        ? `Add sample${compactPrice ? ` · ${compactPrice}` : ''}`
        : `Add to bag${compactPrice ? ` · ${compactPrice}` : ''}`;
      setText(stickySubmit, submit.disabled ? disabledLabel : stickyLabel);
    }

    updateSizeGroups(product);
  };

  const updateStickyPurchaseBar = (product) => {
    const bar = product.querySelector('[data-ml-mobile-purchase-bar]');
    const submit = product.querySelector('.product-form__submit');
    if (!bar || !submit || !mobileQuery.matches) {
      bar?.classList.remove('is-visible');
      bar?.setAttribute('aria-hidden', 'true');
      return;
    }

    const submitRect = submit.getBoundingClientRect();
    const experience = product.querySelector('[data-ml-product-experience]');
    const experienceRect = experience?.getBoundingClientRect();
    const nativeButtonPassed = submitRect.bottom < headerOffset();
    const beforeEditorial = !experienceRect || experienceRect.top > window.innerHeight * 0.2;
    const compareTrayVisible = Boolean(document.querySelector('.ml-compare-tray.is-visible'));
    const visible = nativeButtonPassed && beforeEditorial && !compareTrayVisible;

    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  const initializeProduct = (product) => {
    if (!product) return;
    window.MLProductLayout?.mount(product);
    arrangePurchaseControls(product);
    updateSizeGroups(product);
    setupSampleProxy(product);
    setupMobilePurchaseBar(product);
    updatePurchaseUI(product);
    updateStickyPurchaseBar(product);

    if (!product.dataset.mlObserverReady) {
      product.dataset.mlObserverReady = 'true';
      let frame;
      const observer = new MutationObserver((mutations) => {
        const relevant = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some((node) => node.nodeType === 1)
        );
        if (!relevant) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          window.MLProductLayout?.mount(product);
          arrangePurchaseControls(product);
          setupSampleProxy(product);
          updatePurchaseUI(product);
          updateStickyPurchaseBar(product);
        });
      });
      observer.observe(product, { childList: true, subtree: true });
    }
  };

  const initializeAll = () => products().forEach(initializeProduct);

  document.addEventListener('shopify:section:load', (event) => {
    const product = event.target?.matches?.('product-info.ml-premium-product')
      ? event.target
      : event.target?.querySelector?.('product-info.ml-premium-product');
    if (product) initializeProduct(product);
  });

  document.addEventListener('click', (event) => {
    const groupToggle = event.target.closest('[data-ml-size-group-toggle]');
    if (groupToggle) {
      const product = groupToggle.closest('product-info');
      const group = groupToggle.closest('[data-ml-size-group]');
      if (!product || !group) return;
      const opening = !group.classList.contains('is-open');
      if (mobileQuery.matches) {
        product.querySelectorAll('[data-ml-size-group]').forEach((item) => setGroupOpen(item, item === group && opening));
      } else {
        setGroupOpen(group, true);
      }
      return;
    }

    const sampleProxy = event.target.closest('[data-ml-sample-proxy]');
    if (sampleProxy) {
      const product = sampleProxy.closest('product-info');
      const input = product?.querySelector('.ml-sample-selector input[type="radio"]');
      if (input && !input.disabled && !input.classList.contains('disabled')) input.click();
      return;
    }

    const stickySubmit = event.target.closest('[data-ml-sticky-submit]');
    if (stickySubmit) {
      const product = stickySubmit.closest('product-info');
      const submit = product?.querySelector('.product-form__submit');
      if (submit && !submit.disabled) submit.click();
      return;
    }

    const jump = event.target.closest('.ml-experience__jump a[href^="#"]');
    if (jump) {
      const target = document.querySelector(jump.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }

    const back = event.target.closest('[data-ml-back-to-top]');
    if (back) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
  });

  document.addEventListener('change', (event) => {
    const product = event.target.closest('product-info.ml-premium-product');
    if (!product) return;
    updateSizeGroups(product, event.target.matches('.ml-size-selector input[type="radio"]'));
    requestAnimationFrame(() => updatePurchaseUI(product));
    window.setTimeout(() => updatePurchaseUI(product), 150);
  });

  const updateBackToTop = () => {
    document.querySelectorAll('[data-ml-back-to-top]').forEach((button) => {
      const experience = button.closest('[data-ml-product-experience]');
      const visible = Boolean(experience && experience.getBoundingClientRect().top <= headerOffset());
      button.classList.toggle('is-visible', visible);
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
      button.tabIndex = visible ? 0 : -1;
    });
  };

  let scrollFrame;
  const updateViewportUI = () => {
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(() => {
      updateBackToTop();
      products().forEach(updateStickyPurchaseBar);
    });
  };

  initializeAll();
  updateViewportUI();
  window.addEventListener('scroll', updateViewportUI, { passive: true });
  window.addEventListener('resize', updateViewportUI, { passive: true });
  mobileQuery.addEventListener?.('change', () => {
    initializeAll();
    updateViewportUI();
  });
})();
